import {
  onDisconnect,
  ref,
  remove as rtdbRemove,
  set,
  onValue,
  type Unsubscribe,
  type DataSnapshot,
  type DatabaseReference,
  type Database,
} from 'firebase/database';
import {
  onSnapshot,
  onSnapshotsInSync,
  query,
  collection,
  orderBy,
  limit,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb, getRtdb, isRtdbAvailable } from './firebase';

export interface ConvoSnapshotDoc {
  id: string;
  data: DocumentData;
  hasPendingWrites: boolean;
}

export interface ConvoDocChange {
  type: 'added' | 'modified' | 'removed';
  id: string;
  data: DocumentData;
  /** 로컬 write만 있고 서버 확인 전 — 낙관적 UI의 '전송 중' 판정 (PLAN 5-8-5) */
  hasPendingWrites: boolean;
}

/**
 * 실시간 구독 관리 (PLAN 5-8-9장, 부록 A-6).
 *
 * Firestore 읽기 비용과 배터리를 위해 "활성 대화방 1개"를 강제한다.
 * 라우터가 이 클래스를 통해 subscribe/teardown만 호출하도록 설계.
 */
export class RealtimeManager {
  private unsubs: Unsubscribe[] = [];
  private activeConvoId: string | null = null;
  private visibilityBound = false;

  constructor() {
    this.bindVisibilityEvents();
  }

  private bindVisibilityEvents(): void {
    if (typeof window === 'undefined' || this.visibilityBound) return;
    this.visibilityBound = true;

    // 모바일 기기가 백그라운드로 가거나 화면이 꺼질 때 activeConvo를 즉시 해제하여
    // Functions가 푸시 알림을 '실시간 읽는 중'으로 오판해 누락하는 현상을 방지 (realtime_sysnoti.md 4.3장)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && this.activeConvoId) {
        void setActiveConvo(null);
      } else if (document.visibilityState === 'visible' && this.activeConvoId) {
        void setActiveConvo(this.activeConvoId);
      }
    });

    window.addEventListener('blur', () => {
      if (this.activeConvoId) {
        void setActiveConvo(null);
      }
    });

    window.addEventListener('focus', () => {
      if (document.visibilityState === 'visible' && this.activeConvoId) {
        void setActiveConvo(this.activeConvoId);
      }
    });

    window.addEventListener('pagehide', () => {
      if (this.activeConvoId) {
        void setActiveConvo(null);
      }
    });
  }

  subscribeActiveConvo(
    convoId: string,
    onChange: (change: ConvoDocChange) => void,
    onSnapshotDocs?: (docs: ConvoSnapshotDoc[]) => void,
  ): void {
    this.teardown();
    this.activeConvoId = convoId;

    const q = query(
      collection(getDb(), `convos/${convoId}/messages`),
      orderBy('createdAt', 'desc'),
      limit(30),
    );

    // includeMetadataChanges로 pending/confirmed를 구분한다 (PLAN 5-8-5장).
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        if (onSnapshotDocs) {
          onSnapshotDocs(
            snap.docs.map((doc) => ({
              id: doc.id,
              data: doc.data({ serverTimestamps: 'estimate' }),
              hasPendingWrites: doc.metadata.hasPendingWrites,
            })),
          );
        }
        for (const change of snap.docChanges({ includeMetadataChanges: true })) {
          const doc = change.doc as QueryDocumentSnapshot<DocumentData>;
          onChange({
            type: change.type,
            id: doc.id,
            data: doc.data({ serverTimestamps: 'estimate' }),
            hasPendingWrites: doc.metadata.hasPendingWrites,
          });
        }
      },
      (error) => {
        console.error('[RealtimeManager] subscribeActiveConvo error:', error);
      },
    );

    this.unsubs.push(unsub);
    void setActiveConvo(convoId);
  }

  teardown(): void {
    for (const u of this.unsubs) u();
    this.unsubs = [];
    this.activeConvoId = null;
    void setActiveConvo(null);
  }

  getActiveConvoId(): string | null {
    return this.activeConvoId;
  }

  get listenerCount(): number {
    return this.unsubs.length;
  }
}

let realtimeManager: RealtimeManager | undefined;
export function getRealtimeManager(): RealtimeManager {
  realtimeManager ??= new RealtimeManager();
  return realtimeManager;
}

/**
 * 로컬 캐시가 서버와 모두 동기화된 시점을 알려준다 (PLAN 5-8-6장 연결 상태 UI).
 *
 * Firestore는 재접속과 로컬 큐 flush를 스스로 처리하므로 별도 재동기화 구현이
 * 필요 없다. UI는 이 신호로 "연결 중… → 동기화됨"을 판단한다.
 */
export function observeSnapshotsInSync(cb: () => void): Unsubscribe {
  return onSnapshotsInSync(getDb(), cb);
}

// ── presence ──────────────────────────────────────────────────────────

export interface PresenceState {
  online: boolean;
  activeConvo: string | null;
  device?: string;
  updatedAt: number;
}

/**
 * 접속 상태 등록.
 * onDisconnect로 끊김을 자동 정리하므로, 앱이 죽어도 online이 남지 않는다.
 * 반환값은 cleanup 함수.
 */
export function publishPresence(uid: string, device: string): () => void {
  if (!isRtdbAvailable() || !uid) return () => undefined;
  const r = ref(getRtdb(), `presence/${uid}`);
  onDisconnect(r).remove();
  const mgr = getRealtimeManager();
  const currentActiveConvo =
    typeof document !== 'undefined' && document.visibilityState === 'visible'
      ? mgr.getActiveConvoId()
      : null;
  void set(r, {
    online: true,
    activeConvo: currentActiveConvo,
    device,
    updatedAt: Date.now(),
  });
  return () => {
    void rtdbRemove(r);
  };
}

export function setActiveConvo(convoId: string | null): Promise<void> {
  // databaseURL 미설정 환경 또는 로그아웃 상태에서도 대화방 진입은 성공해야 한다
  const uid = uidFromAuth();
  if (!isRtdbAvailable() || !uid) return Promise.resolve();
  return set(ref(getRtdb(), `presence/${uid}/activeConvo`), convoId);
}

export function observePresence(
  uid: string,
  cb: (state: PresenceState | null) => void,
): Unsubscribe {
  if (!isRtdbAvailable()) return () => undefined;
  const r = ref(getRtdb(), `presence/${uid}`);
  const handler = (snap: DataSnapshot) => cb(snap.val() as PresenceState | null);
  // onValue는 구독 해제 함수를 반환한다 (off() 수동 호출 불필요)
  return onValue(r, handler);
}

// ── 타이핑 표시 (PLAN 5-4장) ───────────────────────────────────────────

export const TYPING_TTL_MS = 4_000;
export const TYPING_THROTTLE_MS = 2_000;
export const TYPING_CLEAR_MS = 1_500;

/** 타이핑 신호 전송에 쓰는 RTDB 경로 (송신은 앱의 useTyping 훅이 담당) */
export function typingRef(convoId: string, uid: string): DatabaseReference {
  return ref(getRtdb(), `typing/${convoId}/${uid}`);
}

/** 대화방의 타이핑 상태 구독 (자기 ID는 항상 제외) */
export function observeTyping(
  convoId: string,
  myUid: string,
  cb: (uids: string[]) => void,
): Unsubscribe {
  if (!isRtdbAvailable()) return () => undefined;
  const r = ref(getRtdb(), `typing/${convoId}`);
  const handler = (snap: DataSnapshot): void => {
    const raw = snap.val() as Record<string, { until: number }> | null;
    if (!raw) return cb([]);
    const now = Date.now();
    cb(
      Object.entries(raw)
        .filter(([uid, v]) => uid !== myUid && v.until > now)
        .map(([uid]) => uid),
    );
  };
  return onValue(r, handler);
}

// ── 내부 헬퍼 ──────────────────────────────────────────────────────────

let cachedUid: string | null = null;

/** presence 경로에 필요한 현재 uid (인증 모듈과 순환 의존을 피하기 위해 전역 주입) */
function uidFromAuth(): string {
  if (!cachedUid) {
    cachedUid = (globalThis as { __talk2nick_uid?: string }).__talk2nick_uid ?? '';
  }
  return cachedUid;
}

export function setCurrentUid(uid: string): void {
  cachedUid = uid;
  (globalThis as { __talk2nick_uid?: string }).__talk2nick_uid = uid;
}

export type { DatabaseReference, Database };
